/**
 * Which events a BD can nominate to right now.
 *
 * One predicate, used by both the "Open for Nomination" count and the New Events cards.
 * They are two renderings of the same answer, and a card list disagreeing with the number
 * above it would leave nobody able to say which is right — so neither owns the rule.
 *
 * Three conditions, all required — four for a caller who nominates:
 *
 *   PUBLISHED               DRAFT is documented as marketing-visible only, so a draft
 *                           would point a BD at an event they cannot even see.
 *   bdNominationSupported   The event takes nominations at all.
 *   today inside the window Both bounds must be present. The service requires them to
 *                           publish a nomination-enabled event, so a published event
 *                           without them is anomalous — and treating a missing bound as
 *                           "open forever" would overstate what a BD can act on.
 *                           The end date is inclusive: each bound is stored as midnight at
 *                           the start of the picked day (utils/eventDates `fromDateInput`), so
 *                           the window closes a day after `nominationEndDate`, at the end of
 *                           the day the marketer picked. Mirrors AWSECEventHub NominationService
 *                           `isNominationWindowOpen`.
 *   the BD's BU is one of   Marketing's rule: a BD nominates only to events for their BU. Any
 *   the event's BUs         of the BD's BUs counts (utils/bdProfile `nominationBusinessUnitsOf`:
 *                           SH_L2 and SH_L3 alike, since the event BU list mixes the two levels),
 *                           compared trimmed and case-insensitively. An event with no BUs is
 *                           closed to every BD — a marketer must say whose it is first. Mirrors
 *                           AWSECEventHub NominationService, which refuses the rest.
 *
 * The fourth applies only when `bdBusinessUnits` is given: pass it for a caller who nominates
 * (canNominate), so their counts, cards and Nominate buttons are what the service will accept;
 * leave it out where the question is "is the window open" for everyone (a marketer's view).
 */

import type { Event } from '../client/EventCalendarClient';

const norm = (bu: string) => bu.trim().toLowerCase();

const DAY_MS = 86_400_000;

/** Whether one of `bdBusinessUnits` is one of the event's BUs. No event BUs: never. */
export function eventIsForBusinessUnits(
  event: Event,
  bdBusinessUnits: readonly string[],
): boolean {
  const mine = new Set(bdBusinessUnits.map(norm).filter(Boolean));
  return (event.businessUnits ?? []).some((bu) => mine.has(norm(bu)));
}

export function isNominatable(
  event: Event,
  now: Date,
  bdBusinessUnits?: readonly string[],
): boolean {
  if (event.contentStatus !== 'PUBLISHED') return false;
  if (!event.bdNominationSupported) return false;
  if (bdBusinessUnits && !eventIsForBusinessUnits(event, bdBusinessUnits)) return false;
  const { nominationStartDate: from, nominationEndDate: to } = event;
  if (!from || !to) return false;
  return from <= now && now.getTime() < to.getTime() + DAY_MS;
}

/**
 * The nominatable events, soonest deadline first.
 *
 * Ordered by when the window CLOSES rather than when the event happens: the card's job is
 * to say what a BD has to act on next, and that is whatever stops accepting nominations
 * first, regardless of which event runs earliest.
 */
export function nominatableEvents(
  events: readonly Event[],
  now: Date,
  bdBusinessUnits?: readonly string[],
): Event[] {
  return events
    .filter((event) => isNominatable(event, now, bdBusinessUnits))
    .sort(
      (a, b) =>
        (a.nominationEndDate?.getTime() ?? 0) - (b.nominationEndDate?.getTime() ?? 0),
    );
}
